import * as moment from "moment";
import {JobEventApiDto, JobBDPChangeEventDataApiDto} from "../../../api/model/rest";
import {getUser} from "../../../store/view/user";
import AuthenticatedEntity from "../../user/AuthenticatedEntity";
import {option, Option} from "ts-option";
import JobEvent, {JobEventTypeEnum} from "./JobEvent";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";

/**
 * Job BDP changed event.
 */
export default class JobBDPChangedEvent extends JobEvent {
	/**
	 * @param {string} id identifier of event.
	 * @param {string} jobId job id.
	 * @param {JobEventTypeEnum} type type of the event.
	 * @param {AuthenticatedEntity} entity entity which creates the event.
	 * @param {moment.Moment} creationDate creation date of event.
	 * @param {string} referenceNumber job reference number.
	 * @param {JobBDPChangedEventData} data job BDP change event data.
	 */
	constructor(public readonly id: string,
		public readonly jobId: string,
		public readonly type: JobEventTypeEnum,
		public readonly entity: AuthenticatedEntity,
		public readonly creationDate: moment.Moment,
		public readonly referenceNumber: string,
		public readonly data: JobBDPChangedEventData) {
		super(id, jobId, type, entity, creationDate, referenceNumber);
	}

	public static fromJson(json: JobEventApiDto): JobBDPChangedEvent {
		const data = JobBDPChangedEventData.fromJson(json.data as JobBDPChangeEventDataApiDto);
		const entity = AuthenticatedEntity.fromJson(json.entity);
		return new JobBDPChangedEvent(json.id, json.jobId, JobEventTypeEnum.BdpAssigneeChanged, entity,
			moment(json.createdAt), json.referenceNumber, data);
	}

	getIcon() {
		return Icon.User;
	}

	getActivityMessage(): string {
		return `Business Development Partner changed from ${this.getPreviousDisplayName()} to ${this.getCurrentDisplayName()}`
	}

	/**
	 * @return {string} display name of previous BDP.
	 */
	getPreviousDisplayName(): string {
		return this.data.previousBusinessDevelopmentPartnerId
			.map(id => getUser(id)
				.map(u => u.getDisplayName())
				.getOrElse("Unassigned")
			).getOrElse("Unassigned");
	}

	/**
	 * @return {string} display name of current BDP.
	 */
	getCurrentDisplayName(): string {
		return this.data.currentBusinessDevelopmentPartnerId
			.map(id => getUser(id)
				.map(u => u.getDisplayName())
				.getOrElse("Unassigned")
			).getOrElse("Unassigned");
	}
}

/**
 * Data specific for business development partner change event.
 */
export class JobBDPChangedEventData {
	/**
	 * @param {Option<string>} previousBusinessDevelopmentPartnerId id of the user being previously assigned as BDP.
	 * @param {Option<string>} currentBusinessDevelopmentPartnerId id of the user being currently assigned as BDP.
	 */
	constructor(public readonly previousBusinessDevelopmentPartnerId: Option<string>,
		public readonly currentBusinessDevelopmentPartnerId: Option<string>) {}

	public static fromJson(json: JobBDPChangeEventDataApiDto): JobBDPChangedEventData {
		return new JobBDPChangedEventData(option(json.previous),
			option(json.current));
	}
}